import {Box, Flex, Heading, IconButton, Text, useDisclosure, useToast} from "@chakra-ui/react";
import moment from "moment/moment";
import {InputLabel, TextareaLabel} from "../../../../components/Theme";
import React, {useEffect, useState} from "react";
import {ReactComponent as DeleteLum} from "../../../../assets/del.svg";
import {Checkbox} from '@chakra-ui/react';
import styles from "./styles";
import DeleteShippingDetailModal from "../../EstimateModals/deleteShippingDetailModal";
import httpRequest from "../../../../core/services/http-request";
import {ReactComponent as SendMessage} from "../../../../assets/unfinishedIcon.svg";
import InputMask from "react-input-mask";
import ToastComponent from "../../../../components/toastComponent/ToastComponent";

const getLuminaireField = (item, fieldKey) => {
    if (item?.lampRecomendedAirtable?.length > 0) {
        try {
            const parsed = JSON.parse(item.lampRecomendedAirtable);
            if (parsed) {
                const val = parsed[fieldKey] || parsed[`FE ${fieldKey}`] || parsed[fieldKey.replace('FE ', '')];
                if (val) return val;
            }
        } catch (e) {
            // Fall through to the Airtable array values.
        }
    }

    const arrays = [
        item?.fixtureRecomendedAirtableArray,
        item?.powerSupplyRecomendedAirtableArray,
        item?.controlRecomendedAirtableArray,
        item?.accessoryRecomendedAirtableArray,
    ];

    for (const arr of arrays) {
        if (arr?.length > 0 && arr[0]?.fields) {
            const fields = arr[0].fields;
            const val = fields[fieldKey] || fields[`FE ${fieldKey}`] || fields[fieldKey.replace('FE ', '')];
            if (val) return val;
        }
    }

    return '';
};

const getCategoryQty = (item, catType, fields) => {
    const fixtureQty = Number(item.recomendedFixtureQty || item.fixtureQty || item.qty || 1);

    if (catType === 'fixture') {
        const fieldQty = fields?.['FE Qty'] || fields?.['FE QTY'] || fields?.['Qty'] || fields?.['QTY'] || fields?.['Quantity'];
        return fieldQty ? Number(fieldQty) : fixtureQty;
    }

    const rawAirtableQty = fields?.['FE Qty'] || fields?.['FE QTY'] || fields?.['Qty'] || fields?.['QTY'] ||
        fields?.['Quantity'] || fields?.['FE Lamp Qty'] || fields?.['FE Power Supply Qty'] ||
        fields?.['FE Control Qty'] || fields?.['FE Accessory Qty'];
    const airtableQty = rawAirtableQty !== undefined && rawAirtableQty !== null && rawAirtableQty !== ''
        ? Number(rawAirtableQty)
        : null;

    let propQty = null;
    if (catType === 'lamp') {
        propQty = item.recomendedLampQty ?? item.lampQty ?? item.lampPerFixture;
    } else if (catType === 'powerSupply') {
        propQty = item.powerSupplyRecomendedQty;
    } else if (catType === 'control') {
        propQty = item.controlRecomendedQty;
    } else if (catType === 'accessory') {
        propQty = item.accessoryRecomendedQty;
    }

    const validAirtableQty = Number.isFinite(airtableQty) && airtableQty > 0;
    const numericPropQty = Number(propQty);
    const multiplier = validAirtableQty
        ? airtableQty
        : (Number.isFinite(numericPropQty) && numericPropQty > 0 ? numericPropQty : 1);

    return multiplier * fixtureQty;
};

const parseAirtableData = (data) => {
    if (!data) return [];
    let parsed = data;
    if (typeof data === 'string') {
        try {
            parsed = JSON.parse(data);
        } catch (e) {
            return [];
        }
    }
    if (!parsed) return [];
    if (!Array.isArray(parsed)) parsed = [parsed];

    return parsed.map(item => {
        const fields = item?.fields || (typeof item === 'object' ? item : {});
        const description = fields['FE Product Description'] || fields['Product Description'] || fields['Description'] || '';
        const orderCode = fields['FE Order Code'] || fields['Order Code'] || fields['Part Number'] || '';
        return {fields, description, orderCode};
    }).filter(item => item.description || item.orderCode);
};

const normalizeIdentity = (value) => String(value ?? '').trim().toLowerCase();

const isSameComponent = (component, shippedItem) => {
    const componentKey = normalizeIdentity(component?.uniqueKey);
    const shippedComponentKey = normalizeIdentity(shippedItem?.uniqueKey);

    if (componentKey && shippedComponentKey) {
        return componentKey === shippedComponentKey;
    }

    const componentId = normalizeIdentity(component?.id);
    const shippedItemId = normalizeIdentity(shippedItem?.id);
    if (componentId && shippedItemId && componentId !== shippedItemId) {
        return false;
    }

    const orderCode = normalizeIdentity(component?.orderCode);
    const shippedOrderCode = normalizeIdentity(shippedItem?.orderCode);
    if (orderCode && shippedOrderCode) {
        return orderCode === shippedOrderCode;
    }

    const description = normalizeIdentity(component?.description);
    const shippedDescription = normalizeIdentity(shippedItem?.description);
    if (description && shippedDescription) {
        return description === shippedDescription;
    }

    return Boolean(componentId && shippedItemId && componentId === shippedItemId);
};

export const getShippingItemsWithAvailability = (luminaires, shippingDetails = []) => {
    if (!Array.isArray(luminaires)) return [];

    const shippedItems = (shippingDetails || []).flatMap(detail => detail?.luminaires || []);
    const result = [];

    const addAvailability = (component) => {
        const allocatedQty = shippedItems.reduce((total, shippedItem) => {
            if (!isSameComponent(component, shippedItem)) return total;

            const quantity = Number(shippedItem?.qtyShippingDetails);
            return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
        }, 0);
        const maxAvailableQty = Math.max(Number(component.totalMaxQty) - allocatedQty, 0);

        result.push({
            ...component,
            allocatedQty,
            availableQty: maxAvailableQty,
            maxAvailableQty,
            qtyShippingDetails: maxAvailableQty,
            isChecked: false,
            error: false,
        });
    };

    luminaires.forEach((item, parentIdx) => {
        const categories = [
            {type: 'fixture', data: item?.fixtureRecomendedAirtableArray || item?.fixtureRecomendedAirtable},
            {type: 'lamp', data: item?.lampRecomendedAirtable || item?.lampRecomendedAirtableArray},
            {type: 'powerSupply', data: item?.powerSupplyRecomendedAirtableArray || item?.powerSupplyRecomendedAirtable},
            {type: 'control', data: item?.controlRecomendedAirtableArray || item?.controlRecomendedAirtable},
            {type: 'accessory', data: item?.accessoryRecomendedAirtableArray || item?.accessoryRecomendedAirtable},
        ];
        let foundAny = false;

        categories.forEach(category => {
            parseAirtableData(category.data).forEach((parsedItem, subIdx) => {
                foundAny = true;
                addAvailability({
                    ...item,
                    description: parsedItem.description,
                    orderCode: parsedItem.orderCode,
                    totalMaxQty: getCategoryQty(item, category.type, parsedItem.fields),
                    uniqueKey: `${item.id || parentIdx}-${category.type}-${subIdx}`,
                });
            });
        });

        if (!foundAny) {
            addAvailability({
                ...item,
                description: item.description || getLuminaireField(item, 'FE Product Description') || '',
                orderCode: item.orderCode || getLuminaireField(item, 'FE Order Code') || '',
                totalMaxQty: Number(item.recomendedFixtureQty || item.qty || 1),
                uniqueKey: `${item.id || parentIdx}-gen-0`,
            });
        }
    });

    return result;
};

const ShippingCreateDetails = ({
                                   estimate,
                                   companyReu,
                                   companySu,
                                   luminaires,
                                   callback,
                                   setShippingPageStatus,
                                   createShippingAllow,
                                   setCreateShippingAllow,
                                   setLoading,
                                   getEstimate
                               }) => {
    const {post} = httpRequest();
    const toast = useToast();
    const [detailsNotes, setDetailsNotes] = useState('');
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [info, setInfo] = useState({
        senderCompanyName: companySu?.name || '',
        senderTrackingLink: 'Tracking Link',
        senderAddress1: companySu?.address || '',
        senderAddress2: companySu?.address2 || '',
        senderCity: companySu?.city || '',
        senderState: companySu?.state || '',
        senderZipCode: companySu?.zipCode || '',
        senderPhone: estimate?.userSu?.phone || '',
        receiverCompanyName: companyReu?.name || '',
        receiverAddress1: companyReu?.address || '',
        receiverAddress2: companyReu?.address2 || '',
        receiverCity: companyReu?.city || '',
        receiverState: companyReu?.state || '',
        receiverZipCode: companyReu?.zipCode || '',
        senderCountry: companySu?.country || 'USA',
        receiverCountry: companyReu?.country || '',
        receiverPhone: estimate?.userReu?.phone || '',
        receiverName: `${estimate?.userReu?.firstName || ''} ${estimate?.userReu?.lastName || ''}`.trim(),
    });

    useEffect(() => {
        setInfo({
            senderCompanyName: companySu?.name || '',
            senderTrackingLink: 'Tracking Link',
            senderAddress1: companySu?.address || '',
            senderAddress2: companySu?.address2 || '',
            senderCity: companySu?.city || '',
            senderState: companySu?.state || '',
            senderZipCode: companySu?.zipCode || '',
            senderPhone: estimate?.userSu?.phone || '',
            receiverCompanyName: companyReu?.name || '',
            receiverAddress1: companyReu?.address || '',
            receiverAddress2: companyReu?.address2 || '',
            receiverCity: companyReu?.city || '',
            receiverState: companyReu?.state || '',
            receiverZipCode: companyReu?.zipCode || '',
            senderCountry: companySu?.country || 'USA',
            receiverCountry: companyReu?.country || '',
            receiverPhone: estimate?.userReu?.phone || '',
            receiverName: `${estimate?.userReu?.firstName || ''} ${estimate?.userReu?.lastName || ''}`.trim(),
        });
    }, [companySu, companyReu, estimate]);

    const [luminairesForDetails, setLuminairesForDetails] = useState(() =>
        getShippingItemsWithAvailability(luminaires, estimate?.shippingDetails)
            .filter(item => item.maxAvailableQty > 0)
    );

    useEffect(() => {
        if (luminaires) {
            setLuminairesForDetails(
                getShippingItemsWithAvailability(luminaires, estimate?.shippingDetails)
                    .filter(item => item.maxAvailableQty > 0)
            );
        }
    }, [luminaires, estimate?.shippingDetails]);

    useEffect(() => {
        if (createShippingAllow) {
            let leds = [];
            for (let i = 0; i < luminairesForDetails?.length; i++) {
                if (luminairesForDetails[i]?.error) {
                    setLoading(false);
                    setCreateShippingAllow(false);
                    return;
                }
                if (luminairesForDetails[i]?.isChecked) {
                    const selectedQty = Number(luminairesForDetails[i].qtyShippingDetails);
                    if (!Number.isFinite(selectedQty) || selectedQty <= 0 || selectedQty > luminairesForDetails[i].maxAvailableQty) {
                        const updated = [...luminairesForDetails];
                        updated[i].error = true;
                        setLuminairesForDetails(updated);
                        setLoading(false);
                        setCreateShippingAllow(false);
                        return;
                    }
                    leds.push(luminairesForDetails[i]);
                }
            }
            if (leds.length === 0) {
                setLoading(false);
                setCreateShippingAllow(false);
                return;
            }
            let data = {
                estimateId: estimate.id,
                luminaires: leds,
                data: info,
                detailsNotes
            };
            post('create-shipping-detail', data).then(() => {
                setShippingPageStatus(1);
                getEstimate();
                setLoading(false);
                toast({
                    render: () => (
                        <ToastComponent title='Shipping Detail Created!' bg='#8FCF74' icon={<SendMessage/>}/>
                    ),
                    duration: 4000,
                    isClosable: true,
                    position: "top-right"
                });
            }).catch(() => {
                setLoading(false);
            });
            setCreateShippingAllow(false);
        }
    }, [createShippingAllow]);

    return (
        <>
            <Flex
                position='relative'
                justifyContent='space-between'>
                <DeleteShippingDetailModal
                    getEstimate={getEstimate}
                    setShippingPageStatus={setShippingPageStatus}
                    onClose={onClose}
                    isOpen={isOpen}/>
                <IconButton
                    onClick={onOpen}
                    height="30px"
                    minW="30px"
                    position="absolute"
                    top="-45px"
                    left="-45px"
                    borderRadius="5px"
                    bg="#FB7575"
                    aria-label="del"
                    icon={<DeleteLum width="15px" height="15px"/>}/>
                <img
                    src="/images/LogoIcon.svg"
                    alt=""/>
                <Flex
                    flexDirection='column'>
                    <Heading
                        m={0}
                        textAlign='end'>
                        Shipping Detail
                    </Heading>
                    <Text textAlign='end'>
                        Reference #: {estimate?.reference}
                    </Text>
                    <Text
                        textAlign='end'>
                        Date : {moment(estimate?.shippingDateCreated?.date).format('MM/DD/YYYY')}
                    </Text>
                </Flex>
            </Flex>
            <Flex
                mt={5}
                justifyContent='space-between'>
                <Flex
                    width='49%'
                    borderRight='2px solid #9C9C9C'
                    flexDirection='column'>
                    <Box as='h2' m={0} mb={3} backgroundColor='#EEEEEE' width='98%' borderRadius='5px' fontSize='18px' pl={4} pt={2} pb={2} color='#222222' fontWeight='700'>
                        Sender Information:
                    </Box>
                    <InputLabel
                        maxW='320px'
                        placeholder={companySu?.name}
                        value={info.senderCompanyName ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, senderCompanyName: e.target.value}))}
                    />
                    <InputLabel
                        maxW='320px'
                        placeholder={'Tracking Link'}
                        value={info.senderTrackingLink ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, senderTrackingLink: e.target.value}))}
                    />
                    <InputLabel
                        maxW='320px'
                        placeholder={companySu?.address}
                        value={info.senderAddress1 ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, senderAddress1: e.target.value}))}
                    />
                    <InputLabel
                        maxW='320px'
                        placeholder={companySu?.address2}
                        value={info.senderAddress2 ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, senderAddress2: e.target.value}))}
                    />
                    <Flex
                        justifyContent='space-between'
                        width='320px'>
                        <InputLabel
                            maxW='150px'
                            placeholder={companySu?.city}
                            value={info.senderCity ?? ''}
                            onChange={(e) => setInfo(prev => ({...prev, senderCity: e.target.value}))}
                        />
                        <InputLabel
                            maxW='150px'
                            placeholder={companySu?.state}
                            value={info.senderState ?? ''}
                            onChange={(e) => setInfo(prev => ({...prev, senderState: e.target.value}))}
                        />
                    </Flex>
                    <Flex
                        justifyContent='space-between'
                        width='320px'>
                        <InputLabel
                            maxW='150px'
                            placeholder={companySu?.zipCode}
                            value={info.senderZipCode ?? ''}
                            onChange={(e) => setInfo(prev => ({...prev, senderZipCode: e.target.value}))}
                        />
                        <InputLabel
                            maxW='150px'
                            placeholder={companySu?.country || 'USA'}
                            value={info.senderCountry ?? ''}
                            onChange={(e) => setInfo(prev => ({...prev, senderCountry: e.target.value}))}
                        />
                    </Flex>
                    <InputLabel
                        mask="999-999-9999"
                        maskChar=""
                        autoComplete="on"
                        placeholder={estimate?.userSu?.phone}
                        value={info.senderPhone ?? ''}
                        as={InputMask}
                        formatChars={{'9': '[0-9]'}}
                        maxW='320px'
                        onChange={(e) => setInfo(prev => ({...prev, senderPhone: e.target.value}))}
                    />
                </Flex>
                <Flex
                    width='50%'
                    flexDirection='column'>
                    <Box as='h2' mt={0} mb={3} backgroundColor='#EEEEEE' width='100%' borderRadius='5px' fontSize='18px' pl={4} pt={2} pb={2} color='#222222' fontWeight='700'>
                        Recipient Information:
                    </Box>
                    <InputLabel
                        maxW='320px'
                        placeholder={companyReu?.name}
                        value={info.receiverCompanyName ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, receiverCompanyName: e.target.value}))}
                    />
                    <InputLabel
                        maxW='320px'
                        placeholder={companyReu?.address}
                        value={info.receiverAddress1 ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, receiverAddress1: e.target.value}))}
                    />
                    <InputLabel
                        maxW='320px'
                        placeholder={companyReu?.address2}
                        value={info.receiverAddress2 ?? ''}
                        onChange={(e) => setInfo(prev => ({...prev, receiverAddress2: e.target.value}))}
                    />
                    <Flex
                        justifyContent='space-between'
                        width='320px'>
                        <InputLabel
                            value={info.receiverCity ?? ''}
                            maxW='150px'
                            placeholder={companyReu?.city}
                            onChange={(e) => setInfo(prev => ({...prev, receiverCity: e.target.value}))}
                        />
                        <InputLabel
                            value={info.receiverState ?? ''}
                            maxW='150px'
                            placeholder={companyReu?.state}
                            onChange={(e) => setInfo(prev => ({...prev, receiverState: e.target.value}))}
                        />
                    </Flex>
                    <Flex
                        justifyContent='space-between'
                        width='320px'>
                        <InputLabel
                            maxW='150px'
                            value={info.receiverZipCode ?? ''}
                            placeholder={companyReu?.zipCode}
                            onChange={(e) => setInfo(prev => ({...prev, receiverZipCode: e.target.value}))}
                        />
                        <InputLabel
                            placeholder={companyReu?.country}
                            value={info.receiverCountry ?? ''}
                            maxW='150px'
                            onChange={(e) => setInfo(prev => ({...prev, receiverCountry: e.target.value}))}
                        />
                    </Flex>
                    <InputLabel
                        maxW='320px'
                        mask="999-999-9999"
                        maskChar=""
                        placeholder={estimate?.userReu?.phone}
                        value={info.receiverPhone ?? ''}
                        autoComplete="on"
                        as={InputMask}
                        formatChars={{'9': '[0-9]'}}
                        onChange={(e) => setInfo(prev => ({...prev, receiverPhone: e.target.value}))}
                    />
                </Flex>
            </Flex>
            <Box as='h2' backgroundColor='#FF9A7A' width='100%' fontSize='18px' fontWeight='700' pl={4} pt={2} pb={2} borderRadius='5px' mt={10}>
                Shipping Information
            </Box>
            <Flex mt={7} mb={7} fontWeight='600'>
                <Text as='h2' m={0} width='10%'>Select</Text>
                <Text as='h2' m={0} ml={2} width='40%'>Description</Text>
                <Text as='h2' m={0} width='30%'>Part Number</Text>
                <Text as='h2' m={0} width='20%'>QTY</Text>
            </Flex>
            {
                luminairesForDetails?.map((item, index) => {
                    return (
                        <Flex mb={4} key={item.uniqueKey || index} backgroundColor='#EEEEEE' border='1px solid #A9A9A9' borderRadius='6px' minH='64px' alignItems='center'>
                            <Text width='10%'>
                                <Checkbox
                                    css={styles.checkbox}
                                    isChecked={!!item.isChecked}
                                    onChange={(e) => {
                                        const updated = [...luminairesForDetails];
                                        updated[index].isChecked = e.target.checked;
                                        setLuminairesForDetails(updated);
                                        callback(updated);
                                    }}
                                    size='lg'
                                    iconColor='black'/>
                            </Text>
                            <Text ml={2} pr={5} width='40%'>
                                {item.description}
                            </Text>
                            <Text width='30%'>
                                {item.orderCode}
                            </Text>
                            <Box mb={2} width='20%'>
                                <InputLabel
                                    maxW='50%'
                                    type='number'
                                    min={0}
                                    max={item.maxAvailableQty}
                                    isInvalid={item.error}
                                    value={item.qtyShippingDetails ?? ''}
                                    onChange={(e) => {
                                        let qty = e.target.value;
                                        const numericQty = Number(qty);
                                        const updated = [...luminairesForDetails];
                                        updated[index].error = qty !== '' && (
                                            !Number.isFinite(numericQty) ||
                                            numericQty < 0 ||
                                            numericQty > item.maxAvailableQty
                                        );
                                        updated[index].qtyShippingDetails = qty;
                                        updated[index].availableQty = Math.max(item.maxAvailableQty - numericQty, 0);
                                        setLuminairesForDetails(updated);
                                        callback(updated);
                                    }}
                                />
                            </Box>
                        </Flex>
                    );
                })
            }
            <TextareaLabel
                minH='56px'
                value={detailsNotes}
                onChange={(e) => setDetailsNotes(e.target.value)}
                backgroundColor='#EEEEEE !important'
                label='Notes'/>
        </>
    );
};

export default ShippingCreateDetails;